import type { Character, CharacterOrigin, CrawlerType, ItemGrant } from '../domain/character.ts';
import type { StatBlock } from '../domain/stats.ts';
import type { ContentRegistry } from '../content/registry.ts';
import type { BackgroundSelection } from '../rules/backgrounds.ts';
import {
  startingSkillsFromBackgrounds,
  validateBackgroundSelections,
} from '../rules/backgrounds.ts';
import type { CombatSelection } from '../rules/startingCombat.ts';
import {
  automaticCombatSkillId,
  mergeStartingSkills,
  startingCombatSkills,
  validateCombatSelection,
} from '../rules/startingCombat.ts';
import { createCharacter } from './characterFactory.ts';
import { applyChanges } from '../rules/characterChanges.ts';
import { batch } from './characterModificationService.ts';
import type { CharacterChange } from '../domain/characterChange.ts';

/**
 * Assembles a rules-legal starting crawler from the player's choices.
 * All legality decisions live in src/rules/; this only wires them together.
 */

export interface CreationDraft {
  id: string;
  name: string;
  crawlerType: CrawlerType;
  size: number;
  stats: StatBlock;
  backgrounds: BackgroundSelection[];
  combat: CombatSelection;
  now: string;
}

export interface CreationProblem {
  step: 'identity' | 'stats' | 'backgrounds' | 'combat';
  message: string;
}

export interface CreationValidation {
  valid: boolean;
  problems: CreationProblem[];
}

export function validateDraft(draft: CreationDraft, registry: ContentRegistry): CreationValidation {
  const problems: CreationProblem[] = [];

  if (draft.name.trim().length === 0) {
    problems.push({ step: 'identity', message: 'The crawler needs a name.' });
  }

  const lookup = {
    table: (id: string) => registry.tablesFor(draft.crawlerType).find((t) => t.id === id),
    background: (id: string) => registry.getBackground(id),
  };

  const backgroundReport = validateBackgroundSelections(
    draft.backgrounds,
    registry.tablesFor(draft.crawlerType),
    lookup,
  );
  for (const problem of backgroundReport.problems) {
    problems.push({ step: 'backgrounds', message: problem.message });
  }

  const takenSkillIds = new Set(draft.backgrounds.flatMap((b) => b.skillIds));
  const combatReport = validateCombatSelection(draft.combat, registry.startingCombat, {
    intelligence: draft.stats.int,
    takenSkillIds,
  });
  for (const problem of combatReport.problems) {
    // A failed prerequisite points the player back at the step that can fix it.
    problems.push({ step: problem.fixAtStep ?? 'combat', message: problem.message });
  }

  return { valid: problems.length === 0, problems };
}

export interface BuiltCharacter {
  character: Character;
  /**
   * What the crawler starts with, for the review screen.
   *
   * DERIVED from the built character's inventory, not accumulated alongside it.
   * A second textual list was how Background starting equipment came to be
   * granted mechanically and yet invisible on the review: the list only ever
   * held the combat-choice strings. There is one source of truth, and this
   * reads it.
   */
  startingItems: ItemGrant[];
}

export function buildCharacter(draft: CreationDraft, registry: ContentRegistry): BuiltCharacter {
  const lookup = {
    table: (id: string) => registry.tablesFor(draft.crawlerType).find((t) => t.id === id),
    background: (id: string) => registry.getBackground(id),
  };

  const backgroundSkills = startingSkillsFromBackgrounds(draft.backgrounds, lookup);
  const combat = startingCombatSkills(draft.combat, registry.startingCombat, draft.crawlerType);
  const skills = mergeStartingSkills([backgroundSkills, combat.skills]);

  // RULE DCC-INV-007 - a Background may hand over starting equipment. What each
  // one granted is recorded ON that Background's entry, so the future
  // origin-editing slice can tell what to unwind. It is NOT inferable from the
  // inventory later: potions get drunk and more get bought.
  const backgroundEntries = draft.backgrounds.map((b) => {
    const definition = registry.getBackground(b.backgroundId);
    return {
      tableId: b.tableId,
      backgroundId: b.backgroundId,
      skillIds: [...b.skillIds],
      grantedItems: (definition?.startingEquipment ?? []).map((g) => ({
        itemId: g.itemId,
        quantity: g.quantity,
      })),
    };
  });

  const origin: CharacterOrigin = {
    backgrounds: backgroundEntries,
    combat: {
      automaticSkillId: automaticCombatSkillId(registry.startingCombat, draft.crawlerType),
      second: draft.combat.second
        ? { kind: draft.combat.second.kind, skillId: draft.combat.second.skillId }
        : null,
    },
    grants: combat.grantItems,
    // Nothing to preserve: this crawler was made after Items existed.
    unmatchedGrants: [],
  };

  // The blank crawler is created with identity and Stats only. Every starting
  // Skill and the creation record are then applied as CHANGES, through the same
  // layer advancement and manual editing use. Starting Skills are therefore
  // validated by exactly the rules that guard a later edit.
  const blank = createCharacter({
    id: draft.id,
    name: draft.name,
    crawlerType: draft.crawlerType,
    stats: draft.stats,
    skills: [],
    size: draft.size,
    origin: null,
    now: draft.now,
  });

  const changes: CharacterChange[] = [
    ...skills.map((s): CharacterChange => ({
      kind: 'add-skill',
      skillId: s.skillId,
      rank: s.rank,
    })),
    { kind: 'set-origin', origin },
    // RULE DCC-INV-007 - the creation grants become ORDINARY inventory, through
    // the same change kind an item picked up on Floor 2 would use. There is no
    // second "starting gear" format: a crawler finishes creation holding
    // entries indistinguishable from ones acquired later.
    //
    // `origin.grants` keeps the historical record of what was handed over, so
    // the future edit-creation-record slice can unwind a changed combat choice.
    ...combineGrants([
      ...combat.grantItems,
      ...backgroundEntries.flatMap((b) => b.grantedItems),
    ]).map((grant): CharacterChange => ({
      kind: 'add-item',
      itemId: grant.itemId,
      quantity: grant.quantity,
    })),
  ];

  const applied = applyChanges(blank, batch('creation', 'Character creation', changes), {
    now: draft.now,
    // Creation's item IDs come from the content package, but checking them here
    // catches a content package that names an Item it does not define.
    catalogue: {
      lookupItem: (id) => registry.getItem(id),
      lookupAttack: (id) => registry.getAttack(id),
    },
  });
  if (!applied.ok) {
    throw new Error(
      `Character creation produced an illegal crawler:\n- ${applied.problems
        .map((p) => p.message)
        .join('\n- ')}`,
    );
  }

  return {
    character: applied.character,
    startingItems: applied.character.inventory.map((entry) => ({
      itemId: entry.itemId,
      quantity: entry.quantity,
    })),
  };
}

/**
 * Merges grants of the same Item into one.
 *
 * Two Backgrounds each handing over a rope is two ropes, not two separate
 * inventory entries for the same ID - `validateInventory` refuses a repeated
 * item, and rightly: "how many ropes do I have" must have one answer.
 *
 * The per-Background history is NOT merged. That records what each one gave.
 */
function combineGrants(grants: readonly ItemGrant[]): ItemGrant[] {
  const merged: ItemGrant[] = [];
  for (const grant of grants) {
    const existing = merged.find((g) => g.itemId === grant.itemId);
    if (existing) existing.quantity += grant.quantity;
    else merged.push({ itemId: grant.itemId, quantity: grant.quantity });
  }
  return merged;
}
